/**
 * RWBR — evidence-weighted re-rating.
 *
 * REFERENCE IMPLEMENTATION. Deliberately dependency-free and framework-free:
 * it is the canonical definition of the rule, not part of the app. Whatever we
 * build later must reproduce these outputs exactly.
 *
 * Implements DECISIONS.md D-008:
 *   1. Sagliano's printed band tables, with the 80% and 0% gaps closed.
 *   2. A minimum-sample rule, so a rating is never rewritten by noise.
 *
 * WHY THE SAMPLE RULE EXISTS
 * A driver who faced TROUBLE twice and failed both is at 0%. Read literally,
 * that hands him ESCAPE 2 — collected in ~97% of incidents — after which he
 * wrecks constantly, drops below the 75% finishing threshold, and is demoted.
 * A career ended by two dice rolls. Sagliano never hit this because a human
 * running it by hand takes one look at a two-roll sample and uses judgement.
 * This function is that judgement, written down.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Band<R> {
  /** Inclusive lower bound, 0-100. */
  min: number;
  /** Inclusive upper bound, 0-100. */
  max: number;
  rating: R;
  ruling?: string;
  note?: string;
}

export interface BandTable<R> {
  name: string;
  source: string;
  input: string;
  /** Observations at or above which the observed rate is trusted outright. */
  fullSample: number;
  fullSampleUnit: string;
  bands: Band<R>[];
  notes?: string[];
  /**
   * Movement cap for thin samples. Weighting alone is not enough: these tables
   * have narrow bands (PIT CREW's A band is 7 points wide), so even a small
   * weight applied to an extreme observation can vault a rating across two or
   * three bands. Discovered by the test suite — an A-grade crew that entered
   * two races and finished neither was being re-graded to C.
   *
   * Tiers, most to least evidence:
   *   weight >= 1                → uncapped; a full season is allowed to say what it says
   *   weight >= nearFullWeight   → nearFullStepCap bands
   *   otherwise                  → thinStepCap bands
   */
  thinStepCap?: number;
  nearFullWeight?: number;
  nearFullStepCap?: number;
}

const DEFAULT_THIN_STEP_CAP = 1;
const DEFAULT_NEAR_FULL_WEIGHT = 0.75;
const DEFAULT_NEAR_FULL_STEP_CAP = 2;

export interface RerateInput<R> {
  table: BandTable<R>;
  /** Successes last season (e.g. escapes made, races finished). */
  successes: number;
  /** Opportunities last season (e.g. TROUBLE rolls faced, races entered). */
  attempts: number;
  /** The rating currently on the card. Used as the prior when evidence is thin. */
  currentRating: R;
  /**
   * D-EZ-92 — percentage points added to the observed rate before it is read
   * against the table, so the rate is judged AGAINST THE LEAGUE rather than in
   * a vacuum. See `leagueNormShift`. 0 (the default) is the page as printed.
   */
  rateShift?: number;
}

export type RerateBasis = 'no-evidence' | 'blended' | 'observed';

export interface RerateResult<R> {
  rating: R;
  basis: RerateBasis;
  /** Raw observed rate, 0-100. `null` when attempts === 0. */
  observedRate: number | null;
  /** Rate implied by the current rating (its band midpoint). `null` if unmappable. */
  priorRate: number | null;
  /** Rate actually used for the lookup. `null` when carried forward. */
  effectiveRate: number | null;
  /** Evidence weight applied to the observation, 0-1. */
  weight: number;
  /** True when the thin-sample movement cap bound the result. */
  capped?: boolean;
  attempts: number;
  fullSample: number;
  /** Plain-language line suitable for an audit trail or the broadcast booth. */
  explanation: string;
}

// ---------------------------------------------------------------------------
// Table helpers
// ---------------------------------------------------------------------------

/**
 * Look up a rate (0-100) in a band table. Throws if the table has a real gap.
 *
 * THE RATE IS FLOORED TO A WHOLE PERCENT FIRST, and that is not a rounding
 * convenience — it is what the printed tables mean.
 *
 * Every band in every one of Sagliano's tables is written in whole percents:
 * "71-62%", "61% & below", "95% & above". Read as closed integer intervals they
 * tile the range exactly, with each band's max one below the next band's min.
 * Read as intervals over the REALS they leave a one-point crack between every
 * pair of adjacent bands, and any observed rate that lands in one — 61.11%,
 * which is 11 finishes from 18 starts — has no rating at all.
 *
 * This was found by the offseason test suite the first time a three-car shop's
 * aggregate finish rate was fed in: a driver's own record tends to produce
 * tidier fractions, so the crack had never been hit. Flooring resolves it the
 * way a person reading the page would: 61-point-anything is 61, and 61 is E.
 * The alternative — widening every band to meet the next — would silently
 * change which side of a boundary a rate falls on.
 */
export function ratingForRate<R>(table: BandTable<R>, rate: number): R {
  if (!Number.isFinite(rate)) throw new RangeError(`rate must be finite, got ${rate}`);
  if (rate < 0 || rate > 100) throw new RangeError(`rate must be 0-100, got ${rate}`);
  const whole = Math.floor(rate);
  const band = table.bands.find((b) => whole >= b.min && whole <= b.max);
  if (!band) {
    throw new Error(
      `${table.name}: no band covers ${whole}% (from ${rate}%). The band table has a gap — this is ` +
        `exactly the class of defect D-008 exists to prevent. Fix the table, do not special-case the caller.`,
    );
  }
  return band.rating;
}

/**
 * The rate a rating implies — the midpoint of its band.
 *
 * Midpoint is used rather than either edge because it is the least-committed
 * reading of "this is what the card currently says about him", and it keeps the
 * blend symmetric: thin evidence pulls equally in both directions.
 */
export function rateForRating<R>(table: BandTable<R>, rating: R): number | null {
  const band = table.bands.find((b) => b.rating === rating);
  if (!band) return null;
  return (band.min + band.max) / 2;
}

// ---------------------------------------------------------------------------
// The rule
// ---------------------------------------------------------------------------

/**
 * Re-rate one attribute from last season's performance, weighting the
 * observation by how much of it there was.
 *
 *   attempts === 0            → carry the rating forward untouched
 *   attempts >= fullSample    → trust the observed rate outright
 *   otherwise                 → blend toward the current rating in proportion
 *                               to the evidence:
 *                                 w    = attempts / fullSample
 *                                 rate = observed·w + prior·(1 − w)
 */
export function evidenceWeightedRerate<R>(input: RerateInput<R>): RerateResult<R> {
  const { table, successes, attempts, currentRating } = input;

  if (!Number.isInteger(attempts) || attempts < 0) {
    throw new RangeError(`attempts must be a non-negative integer, got ${attempts}`);
  }
  if (!Number.isInteger(successes) || successes < 0) {
    throw new RangeError(`successes must be a non-negative integer, got ${successes}`);
  }
  if (successes > attempts) {
    throw new RangeError(`successes (${successes}) cannot exceed attempts (${attempts})`);
  }

  // No opportunities at all: nothing happened, so nothing is learned.
  if (attempts === 0) {
    return {
      rating: currentRating,
      basis: 'no-evidence',
      observedRate: null,
      priorRate: rateForRating(table, currentRating),
      effectiveRate: null,
      weight: 0,
      capped: false,
      attempts: 0,
      fullSample: table.fullSample,
      explanation:
        `${table.name} unchanged at ${String(currentRating)} — no ${table.fullSampleUnit} ` +
        `last season, so there is nothing to re-rate from.`,
    };
  }

  const rawRate = (successes / attempts) * 100;
  const shift = input.rateShift ?? 0;
  const observedRate = Math.min(100, Math.max(0, rawRate + shift));
  const normNote =
    shift === 0 ? '' : ` (${rawRate.toFixed(1)}% raw, ${shift > 0 ? '+' : ''}${shift.toFixed(1)} to the league norm)`;

  // Enough evidence: take it at face value.
  if (attempts >= table.fullSample) {
    const rating = ratingForRate(table, observedRate);
    return {
      rating,
      basis: 'observed',
      observedRate,
      priorRate: rateForRating(table, currentRating),
      effectiveRate: observedRate,
      weight: 1,
      capped: false,
      attempts,
      fullSample: table.fullSample,
      explanation:
        `${table.name} → ${String(rating)} on ${successes} of ${attempts} ` +
        `(${observedRate.toFixed(1)}%${normNote}) — a full season's evidence.`,
    };
  }

  // Thin evidence: blend toward what the card already says.
  const prior = rateForRating(table, currentRating);
  if (prior === null) {
    // Current rating isn't in this table (e.g. a rulebook-legal ESCAPE 12 that
    // Sagliano's table never produces). Fall back to the observation rather
    // than silently inventing a prior.
    const rating = ratingForRate(table, observedRate);
    return {
      rating,
      basis: 'observed',
      observedRate,
      priorRate: null,
      effectiveRate: observedRate,
      weight: 1,
      capped: false,
      attempts,
      fullSample: table.fullSample,
      explanation:
        `${table.name} → ${String(rating)} on ${successes} of ${attempts} ` +
        `(${observedRate.toFixed(1)}%). Current rating ${String(currentRating)} is outside this ` +
        `table, so no prior was available to blend against.`,
    };
  }

  const weight = attempts / table.fullSample;
  const effectiveRate = observedRate * weight + prior * (1 - weight);
  const uncapped = ratingForRate(table, effectiveRate);
  const { rating, capped, maxSteps } = applyStepCap(table, currentRating, uncapped, weight);

  return {
    rating,
    basis: 'blended',
    observedRate,
    priorRate: prior,
    effectiveRate,
    weight,
    attempts,
    fullSample: table.fullSample,
    capped,
    explanation:
      `${table.name} → ${String(rating)}. Only ${attempts} ${table.fullSampleUnit} ` +
      `(${successes} good, ${observedRate.toFixed(1)}%${normNote}) — too thin to judge on alone, so it counts ` +
      `${(weight * 100).toFixed(0)}% against a card that says ${prior.toFixed(0)}%, ` +
      `landing at ${effectiveRate.toFixed(1)}%` +
      (capped
        ? `, and held to ${maxSteps} band${maxSteps === 1 ? '' : 's'} of movement on that little evidence ` +
          `(would otherwise have been ${String(uncapped)}).`
        : `.`),
  };
}

/**
 * Hold a thin-evidence result to a limited number of bands of movement.
 *
 * Weighting alone is insufficient because band widths vary: PIT CREW's A band
 * spans 7 points while its E band spans 62. A 17% weight on a 0% observation
 * moves an A-grade crew 16 points, which crosses two bands. This cap encodes
 * the actual intent of D-008 — thin evidence may nudge a rating, never leap it.
 */
function applyStepCap<R>(
  table: BandTable<R>,
  currentRating: R,
  proposed: R,
  weight: number,
): { rating: R; capped: boolean; maxSteps: number } {
  const thin = table.thinStepCap ?? DEFAULT_THIN_STEP_CAP;
  const nearW = table.nearFullWeight ?? DEFAULT_NEAR_FULL_WEIGHT;
  const nearCap = table.nearFullStepCap ?? DEFAULT_NEAR_FULL_STEP_CAP;
  const maxSteps = weight >= nearW ? nearCap : thin;

  const ordered = [...table.bands].sort((a, b) => a.min - b.min);
  const curIdx = ordered.findIndex((b) => b.rating === currentRating);
  const propIdx = ordered.findIndex((b) => b.rating === proposed);
  if (curIdx === -1 || propIdx === -1) return { rating: proposed, capped: false, maxSteps };

  const delta = propIdx - curIdx;
  if (Math.abs(delta) <= maxSteps) return { rating: proposed, capped: false, maxSteps };

  const cappedIdx = curIdx + Math.sign(delta) * maxSteps;
  return { rating: ordered[cappedIdx].rating, capped: true, maxSteps };
}

/**
 * D-EZ-92 — HOW FAR THE LEAGUE AS A WHOLE SITS FROM ITS OWN CARDS.
 *
 * SAG/8 reads next season's ESCAPE straight off this season's escape rate. But
 * the rate a driver OBSERVES is not the rate his card promises: track penalties,
 * duels, feud/fume rolls and every other modifier pull the whole field's escape
 * rate below the nominal 2d6 odds of the numbers printed on its cards. Read in a
 * vacuum, that gap hands nearly everyone a lower ESCAPE every winter; lower
 * ESCAPE means more wrecks, which means a lower rate next year — the attrition
 * spiral measured in the 4 Oct 2026 audit (escape 8.36 → 6.3 and DNFs 8 → 14 a
 * race over ten seasons on seed 77).
 *
 * The fix judges each driver against the LEAGUE: the shift is the gap between
 * what the field's cards imply (each card's band midpoint, weighted by how many
 * rolls he faced) and what the field actually did. A league-average driver
 * keeps a league-average card; a driver who escaped more than his peers still
 * goes up, and one who escaped less still comes down. The league itself no
 * longer drifts.
 */
export function leagueNormShift<R>(
  table: BandTable<R>,
  drivers: readonly { successes: number; attempts: number; currentRating: R }[],
): number {
  let attempts = 0;
  let successes = 0;
  let implied = 0;
  for (const d of drivers) {
    if (d.attempts <= 0) continue;
    const prior = rateForRating(table, d.currentRating);
    if (prior === null) continue;
    attempts += d.attempts;
    successes += d.successes;
    implied += prior * d.attempts;
  }
  if (attempts === 0) return 0;
  return implied / attempts - (successes / attempts) * 100;
}

/**
 * D-EZ-92 — THE SHIFT THAT KEEPS THE LEAGUE'S AVERAGE CARD WHERE IT IS.
 *
 * `leagueNormShift` lines the league's pooled RATE up with its cards, but that
 * alone was measured to still lose 0.1-0.3 of a point of mean ESCAPE every
 * winter: the bands are bounded (an 11 can only go down, a 2 only up) and a
 * season's sample is noisy, so the re-rated cards regress toward the middle of
 * the TABLE rather than toward the league. Ten winters of that is the old
 * spiral again, slower.
 *
 * So the shift is calibrated instead: the one value, found by bisection, at
 * which the league's mean re-rated card equals its mean card going in. The
 * league's average stays put; who is above it and who below is decided by the
 * season, exactly as SAG/8 intends. Numeric ratings only.
 */
export function calibratedNormShift(
  table: BandTable<number>,
  drivers: readonly { successes: number; attempts: number; currentRating: number }[],
): number {
  const judged = drivers.filter((d) => d.attempts > 0 && rateForRating(table, d.currentRating) !== null);
  if (judged.length === 0) return 0;
  const target = judged.reduce((n, d) => n + d.currentRating, 0) / judged.length;
  const meanAt = (shift: number): number =>
    judged.reduce((n, d) => n + evidenceWeightedRerate({ table, ...d, rateShift: shift }).rating, 0) / judged.length;
  let lo = -50;
  let hi = 50;
  if (meanAt(lo) >= target) return lo;
  if (meanAt(hi) <= target) return hi;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (meanAt(mid) < target) lo = mid;
    else hi = mid;
  }
  // `hi` is the smallest shift found that reaches the target; take whichever
  // side of the step lands nearer.
  return Math.abs(meanAt(lo) - target) < Math.abs(meanAt(hi) - target) ? lo : hi;
}

// ---------------------------------------------------------------------------
// Integrity check — run this over any band table before trusting it
// ---------------------------------------------------------------------------

export interface TableAudit {
  ok: boolean;
  problems: string[];
}

/**
 * Verify a band table partitions 0-100 with no gaps, no overlaps, and no
 * inverted bounds. The missing 80% in the printed Escape table is precisely
 * what this catches.
 */
export function auditBandTable<R>(table: BandTable<R>): TableAudit {
  const problems: string[] = [];
  const bands = [...table.bands].sort((a, b) => a.min - b.min);

  for (const b of bands) {
    if (b.min > b.max) problems.push(`inverted band ${b.min}-${b.max} → ${String(b.rating)}`);
    if (b.min < 0 || b.max > 100) problems.push(`band ${b.min}-${b.max} escapes 0-100`);
  }
  if (bands.length === 0) {
    problems.push('table has no bands');
    return { ok: false, problems };
  }
  if (bands[0].min !== 0) problems.push(`table starts at ${bands[0].min}%, not 0%`);
  if (bands[bands.length - 1].max !== 100) {
    problems.push(`table ends at ${bands[bands.length - 1].max}%, not 100%`);
  }
  for (let i = 1; i < bands.length; i++) {
    const prev = bands[i - 1];
    const cur = bands[i];
    if (cur.min > prev.max + 1) {
      problems.push(`GAP: nothing covers ${prev.max + 1}-${cur.min - 1}%`);
    } else if (cur.min <= prev.max) {
      problems.push(`OVERLAP: ${cur.min}-${prev.max}% is in two bands`);
    }
  }
  const seen = new Set<R>();
  for (const b of bands) {
    if (seen.has(b.rating)) problems.push(`rating ${String(b.rating)} appears in more than one band`);
    seen.add(b.rating);
  }

  return { ok: problems.length === 0, problems };
}
